import type { Role } from '@lucidtech/las-sdk-browser';
import { memo, useCallback, useState } from 'react';
import { FieldError, Form, Key } from 'react-aria-components';

import CloseIcon from '@/assets/close-icon.svg?react';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  ErrorCard,
  Flex,
  Heading,
  Input,
  Label,
  ListBoxItem,
  ListBoxItemProps,
  SelectPills,
  SelectPillsOption,
  Text,
  TextField,
} from '@/components/core';
import { AdminIcon, DeveloperIcon, ReviewerIcon, SaveIcon } from '@/components/icons';
import { ManagedRole } from '@/constants';
import { toProfileId, toUserId, useCreateUser, useGetProfile, useGetUser, useListRoles } from '@/hooks/api';
import { DivProps } from '@/utils';

import { RoleBadge, RoleIcon } from './RolesTable';

export const RoleItem = <T extends Role>({ value, ...rest }: ListBoxItemProps<T>) => {
  const roleNameMapping = {
    Administrator: 'Administrator',
    Member: 'Developer',
    Validator: 'Reviewer',
  };

  const roleDescriptions = {
    Administrator: 'Full access to all workspace features and settings.',
    Member: 'Can create and edit agents and invite reviewers. Cannot change workspace settings.',
    Validator: 'Can review flagged documents. Cannot change workspace settings or edit agents.',
  };

  const roleIcons = {
    Administrator: <AdminIcon className="!size-5 min-w-5" />,
    Member: <DeveloperIcon className="!size-5 min-w-5" />,
    Validator: <ReviewerIcon className="!size-5 min-w-5" />,
  };

  return (
    <ListBoxItem value={value} {...rest}>
      {roleIcons[value?.name] ?? <AdminIcon className="!size-5 min-w-5" />}

      <Flex col className="grow items-start text-start" variant="plain">
        <Heading size="md" slot="title">
          {roleNameMapping[value?.name] ?? value?.name}
        </Heading>
        <Text slot="description" className="!font-regular text-sm !text-gray-600">
          {value?.description ?? roleDescriptions[value?.name] ?? 'Custom role with specific permissions.'}
        </Text>
      </Flex>
    </ListBoxItem>
  );
};

export type InviteMembersProps = DivProps & {
  close?: () => void;
  defaultRole?: ManagedRole;
  userId?: string;
};

const IInviteMembers = ({ close, userId, defaultRole, ...rest }: InviteMembersProps) => {
  const { data: roles, isLoading } = useListRoles();
  const { mutate: createUser, isPending, error } = useCreateUser();
  const { data: user } = useGetUser(toUserId(userId));
  const { data: profile } = useGetProfile(toProfileId(user?.profileId));

  const [selectedRoles, setSelectedRoles] = useState<Key[] | null>(defaultRole ? [defaultRole] : []);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const formData = Object.fromEntries(new FormData(e.currentTarget));
      const email = formData?.email?.toString();
      const roleIds = selectedRoles?.map((r) => r.toString()) ?? [];

      if (roleIds.length > 0 && email) {
        createUser({ email, roleIds }, { onSuccess: close });
      }
    },
    [close, createUser, selectedRoles]
  );

  return (
    <Card {...rest} className="w-100">
      <CardHeader>
        <Flex col className="grow">
          <Heading size="lg">Invite member</Heading>
          <Text>Invite co-workers to your workspace.</Text>
        </Flex>

        <Button className="hover:bg-gray-100" variant="plain" onPress={close}>
          <CloseIcon className="size-md text-gray-400" />
        </Button>
      </CardHeader>

      <Form onSubmit={onSubmit}>
        <CardBody col>
          <TextField
            className="flex flex-col"
            name="email"
            type="email"
            isRequired
            defaultValue={profile?.email}
            key={profile?.email}
          >
            <Label isRequired>Email</Label>
            <Input placeholder="jane.doe@acme.com" />
            <FieldError className="text-error-500 text-sm" />
          </TextField>

          <SelectPills
            label="Roles"
            isRequired
            onChange={setSelectedRoles}
            defaultValue={defaultRole ? [defaultRole] : []}
            items={roles?.data.map((a) => ({ ...a, id: a.roleId }))}
            itemRenderFn={(item) => <RoleBadge key={item?.id} roleId={item?.roleId} />}
            optionRenderFn={(option) => (
              <SelectPillsOption value={option}>
                <Flex className="gap-xs items-center">
                  <RoleIcon roleId={option?.roleId} className="size-3" />
                  <Text>{option?.name}</Text>
                </Flex>
              </SelectPillsOption>
            )}
          />
        </CardBody>

        <CardFooter separator>
          <Button variant="primary" className="w-full" isDisabled={isLoading || isPending} type="submit">
            {isPending && <SaveIcon />}
            <Text>Send invite</Text>
          </Button>
          {error && <ErrorCard label="Error while inviting user" error={error} />}
        </CardFooter>
      </Form>
    </Card>
  );
};

export const InviteMembers = memo(IInviteMembers) as typeof IInviteMembers;
